/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.variantSelect = this.form.querySelector('select[name="id"]')
    this.cardVariants = (this.dataset.cardVariants || this.form.dataset.cardVariants)
      ? JSON.parse(this.dataset.cardVariants || this.form.dataset.cardVariants)
      : []
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.stickyAtcBar = this.form.querySelector('[data-product-sticky-atc]')
    this.stickyAtcBtn = this.form.querySelector('[data-product-sticky-atc-btn]')
    this.atcBlock = this.form.querySelector('.product-block-atc')
    this.buyBtn = this.form.querySelector('.btn-buy')
    this.productPageMediaQuery = window.matchMedia('(max-width: 767px)')

    this.form.addEventListener('submit', event => this.onSubmit(event))

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event)))

    this.variantSelect?.addEventListener('change', event => this.onChangeCardVariant(event))

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))

    this.handleStickyViewportChange = this.handleStickyViewportChange.bind(this)
    this.resetAtcSuccessHandler = null
    this.initStickyAtc()
  }

  async onChangeProductOption (event) {
    event.preventDefault()

    const variants = JSON.parse(this.form.dataset.variants)
    const optionNames = [...new Set([...this.productOptions].map(elem => elem.name))]
    const changedOptionIndex = optionNames.indexOf(event.target.name)
    const selectedOptions = []

    this.productOptions.forEach(elem => {
      if (elem.type === 'radio' && elem.checked) {
        selectedOptions.push(elem.value)
      } else if (elem.type === 'select-one') {
        selectedOptions.push(elem.value)
      }
    })

    let selectedVariant = variants.find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    )

    if (!selectedVariant && this.redirectToSelectedOptionValues()) {
      return
    }

    if (!selectedVariant && changedOptionIndex >= 0) {
      selectedVariant = variants.find(variant =>
        variant.available && variant.options[changedOptionIndex] === selectedOptions[changedOptionIndex]
      ) || variants.find(variant =>
        variant.options[changedOptionIndex] === selectedOptions[changedOptionIndex]
      )

      if (selectedVariant) {
        this.syncOptionsToVariant(selectedVariant, optionNames)
      }
    }

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)

    if (selectedVariant) {
      this.querySelector('[name="id"]').value = selectedVariant.id

      if (this.closest('.product-main') && !window.Shopify?.designMode) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
        detail: selectedVariant
      }))
    }
  }

  redirectToSelectedOptionValues () {
    if (!this.closest('.product-main') || window.Shopify?.designMode) return false

    const selectedOptionValueIds = [...this.productOptions]
      .filter(elem => (elem.type === 'radio' && elem.checked) || elem.type === 'select-one')
      .map(elem => elem.dataset.optionValueId)
      .filter(Boolean)

    const optionNames = [...new Set([...this.productOptions].map(elem => elem.name))]

    if (selectedOptionValueIds.length !== optionNames.length) return false

    const url = new URL(window.location.href)
    url.searchParams.delete('variant')
    url.searchParams.set('option_values', selectedOptionValueIds.join(','))
    window.location.href = url.toString()

    return true
  }

  syncOptionsToVariant (selectedVariant, optionNames) {
    selectedVariant.options.forEach((optionValue, index) => {
      const optionName = optionNames[index]
      if (!optionName) return

      this.productOptions.forEach(elem => {
        if (elem.name !== optionName) return
        if (elem.type === 'radio') {
          elem.checked = elem.value === optionValue
        } else if (elem.type === 'select-one') {
          elem.value = optionValue
        }
      })
    })
  }

  onChangeCardVariant (event) {
    event.preventDefault()

    const selectedVariant = this.cardVariants.find(variant =>
      String(variant.id) === String(this.variantSelect.value)
    )

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)
    this.adjustCardImage(selectedVariant)
  }

  adjustPricing (selectedVariant) {
    const pricingScope = this.closest('.product-card') || this.form
    const regularPriceBlock = pricingScope.querySelector('.product-price')
    const comparePrice = pricingScope.querySelector('.product-price-compare')
    const finalPrice = pricingScope.querySelector('.product-price-final')
    const compareBadge = pricingScope.querySelector('.product-sale-badge')
    const soldOutBadge = pricingScope.querySelector('.product-sold-out-badge')
    const firstOrderPriceNote = pricingScope.querySelector('[data-first-order-price-note]')
    const firstOrderOriginalPrice = firstOrderPriceNote?.querySelector('[data-first-order-original-price]')
    const firstOrderPriceValue = firstOrderPriceNote?.querySelector('[data-first-order-price-value]')
    const firstOrderPriceLabel = firstOrderPriceNote?.querySelector('[data-first-order-price-label]')
    const firstOrderPriceNoteText = firstOrderPriceNote?.querySelector('[data-first-order-price-note-text]')
    const firstOrderPriceState = firstOrderPriceNote?.dataset.priceState
    const firstOrderDiscountRate = Number(firstOrderPriceNote?.dataset.firstOrderDiscountRate || '1')
    const firstOrderDiscountPercent = Number(firstOrderPriceNote?.dataset.firstOrderDiscountPercent || '0')
    const firstOrderPriceLabelText = firstOrderPriceNote?.dataset.firstOrderPriceLabel || ''
    const firstOrderNoteTextValue = firstOrderPriceNote?.dataset.firstOrderNoteText || ''

    if (!selectedVariant) {
      if (comparePrice) {
        comparePrice.textContent = ''
        comparePrice.setAttribute('hidden', 'hidden')
      }
      if (finalPrice) {
        finalPrice.textContent = ''
        finalPrice.classList.remove('product-price-final-sale')
      }
      if (compareBadge) {
        compareBadge.textContent = ''
        compareBadge.setAttribute('hidden', 'hidden')
      }
      soldOutBadge?.setAttribute('hidden', 'hidden')
      firstOrderPriceNote?.setAttribute('hidden', 'hidden')
      if (firstOrderOriginalPrice) firstOrderOriginalPrice.textContent = ''
      if (firstOrderPriceValue) firstOrderPriceValue.textContent = ''
      if (firstOrderPriceLabel) firstOrderPriceLabel.textContent = ''
      if (firstOrderPriceNoteText) firstOrderPriceNoteText.textContent = ''
      regularPriceBlock?.removeAttribute('hidden')
      return
    }

    if (selectedVariant.compare_at_price > selectedVariant.price && comparePrice && finalPrice && compareBadge) {
      comparePrice.textContent = window.Shopify.formatMoneyWithoutTrailingZeros(selectedVariant.compare_at_price)
      comparePrice.removeAttribute('hidden')
      finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}</span>${window.Shopify.formatMoneyWithoutTrailingZeros(selectedVariant.price)}`
      finalPrice.classList.add('product-price-final-sale')
      compareBadge.removeAttribute('hidden')
      if (compareBadge.dataset.discountType === 'percentage') {
        const savings = 100 - (Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100)) + '%'
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      } else {
        const savings = window.Shopify.formatMoneyWithoutTrailingZeros(selectedVariant.compare_at_price - selectedVariant.price)
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      }
    } else {
      if (comparePrice) {
        comparePrice.textContent = ''
        comparePrice.setAttribute('hidden', 'hidden')
      }
      if (finalPrice) {
        finalPrice.innerHTML = window.Shopify.formatMoneyWithoutTrailingZeros(selectedVariant.price)
        finalPrice.classList.remove('product-price-final-sale')
      }
      if (compareBadge) {
        compareBadge.textContent = ''
        compareBadge.setAttribute('hidden', 'hidden')
      }
    }

    if (selectedVariant.available) {
      soldOutBadge.setAttribute('hidden', 'hidden')
    } else {
      soldOutBadge.removeAttribute('hidden')
    }

    if (firstOrderPriceNote) {
      if ((firstOrderPriceState === 'estimate' || firstOrderPriceState === 'signup') && firstOrderPriceValue) {
        if (firstOrderOriginalPrice) {
          firstOrderOriginalPrice.textContent = window.Shopify.formatMoneyWithoutTrailingZeros(selectedVariant.price)
        }
        const firstOrderPriceUnits = Math.round(selectedVariant.price / 100)
        const firstOrderDiscountAmountUnits = firstOrderDiscountPercent > 0
          ? Math.floor(firstOrderPriceUnits * firstOrderDiscountPercent / 100)
          : firstOrderPriceUnits - Math.ceil(firstOrderPriceUnits * firstOrderDiscountRate)
        firstOrderPriceValue.textContent = window.Shopify.formatMoneyWithoutTrailingZeros((firstOrderPriceUnits - firstOrderDiscountAmountUnits) * 100)
        if (firstOrderPriceLabel) {
          firstOrderPriceLabel.textContent = firstOrderPriceLabelText
        }
        if (firstOrderPriceNoteText) {
          firstOrderPriceNoteText.textContent = firstOrderNoteTextValue
        }
        firstOrderPriceNote.removeAttribute('hidden')
        if (!firstOrderPriceNote.classList.contains('first-order-price-note-product')) {
          regularPriceBlock?.setAttribute('hidden', 'hidden')
        } else {
          regularPriceBlock?.removeAttribute('hidden')
        }
      } else {
        firstOrderPriceNote.setAttribute('hidden', 'hidden')
        regularPriceBlock?.removeAttribute('hidden')
      }
    } else {
      regularPriceBlock?.removeAttribute('hidden')
    }
  }

  adjustCardImage (selectedVariant) {
    const productCardImage = this.closest('.product-card')?.querySelector('.product-card-img-wrapper img')

    if (!productCardImage || !selectedVariant?.featured_image_src) return

    productCardImage.removeAttribute('srcset')
    productCardImage.removeAttribute('sizes')
    productCardImage.src = selectedVariant.featured_image_src
    if (selectedVariant.featured_image_alt) {
      productCardImage.alt = selectedVariant.featured_image_alt
    }
  }

  adjustButtons (selectedVariant) {
    this.atcBtn?.classList.remove('product-atc-added')
    this.stickyAtcBtn?.classList.remove('product-atc-added')
    this.atcBtn?.removeAttribute('aria-live')
    this.stickyAtcBtn?.removeAttribute('aria-live')

    if (selectedVariant) {
      if (selectedVariant.available) {
        this.atcBtn.removeAttribute('disabled')
        this.stickyAtcBtn?.removeAttribute('disabled')
        this.buyBtn?.removeAttribute('disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.add_to_cart
        if (this.stickyAtcBtn) this.stickyAtcBtn.innerHTML = window.theme.locales.product.add_to_cart
      } else {
        this.atcBtn.setAttribute('disabled', 'disabled')
        this.stickyAtcBtn?.setAttribute('disabled', 'disabled')
        this.buyBtn?.setAttribute('disabled', 'disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.sold_out
        if (this.stickyAtcBtn) this.stickyAtcBtn.innerHTML = window.theme.locales.product.sold_out
      }
    } else {
      this.atcBtn.setAttribute('disabled', 'disabled')
      this.stickyAtcBtn?.setAttribute('disabled', 'disabled')
      this.buyBtn?.setAttribute('disabled', 'disabled')
      this.atcBtn.innerHTML = window.theme.locales.product.unavailable
      if (this.stickyAtcBtn) this.stickyAtcBtn.innerHTML = window.theme.locales.product.unavailable
    }
  }

  async onSubmit (event) {
    event.preventDefault()

    const submitter = event.submitter || this.atcBtn
    this.clearAtcSuccessState()

    submitter?.classList.add('loading')
    submitter?.setAttribute('aria-busy', 'true')
    if (submitter) submitter.disabled = true

    const formData = new FormData(this.form)
    const cartContainer = document.querySelector('cart-container')
    const result = await cartContainer?.add(formData)

    submitter?.classList.remove('loading')
    submitter?.setAttribute('aria-busy', 'false')

    const selectedVariantId = this.form.querySelector('[name="id"]')?.value
    const selectedVariant = JSON.parse(this.form.dataset.variants).find(variant =>
      String(variant.id) === String(selectedVariantId)
    )

    if (result?.ok && submitter) {
      this.showAtcSuccessState(selectedVariant)
    } else {
      this.adjustButtons(selectedVariant)
    }
  }

  onClickBuyBtn (event) {
    if (window.Shopify?.designMode) {
      event?.preventDefault()
      return
    }

    this.buyBtn.classList.add('loading')
    const variantId = this.form.querySelector('[name="id"]').value
    const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1)
    window.location.href = `/cart/${variantId}:${qty}`
  }

  initStickyAtc () {
    if (!this.stickyAtcBar || !this.atcBlock || !this.closest('.product-main') || this.closest('.quick-view-modal')) return

    this.stickyObserver = new IntersectionObserver(([entry]) => {
      const shouldShow = this.productPageMediaQuery.matches && !entry.isIntersecting
      this.stickyAtcBar.classList.toggle('is-visible', shouldShow)
      document.body.classList.toggle('product-sticky-atc-visible', shouldShow)
    }, {
      threshold: 0.2
    })

    this.stickyObserver.observe(this.atcBlock)
    this.productPageMediaQuery.addEventListener('change', this.handleStickyViewportChange)
    this.handleStickyViewportChange()
  }

  handleStickyViewportChange () {
    if (!this.productPageMediaQuery.matches) {
      this.stickyAtcBar?.classList.remove('is-visible')
      document.body.classList.remove('product-sticky-atc-visible')
    }
  }

  clearAtcSuccessState () {
    if (!this.resetAtcSuccessHandler) return

    window.removeEventListener('scroll', this.resetAtcSuccessHandler)
    this.resetAtcSuccessHandler()
  }

  showAtcSuccessState (selectedVariant) {
    const buttons = [this.atcBtn, this.stickyAtcBtn].filter(Boolean)
    const successMarkup = '<span class="product-atc-success-icon" aria-hidden="true">✓</span><span>追加済み</span>'

    buttons.forEach(btn => {
      btn.classList.add('product-atc-added')
      btn.innerHTML = successMarkup
      btn.disabled = true
      btn.setAttribute('aria-live', 'polite')
    })

    this.resetAtcSuccessHandler = () => {
      this.resetAtcSuccessHandler = null
      buttons.forEach(btn => {
        btn.classList.remove('product-atc-added')
        btn.removeAttribute('aria-live')
      })
      this.adjustButtons(selectedVariant)
    }

    window.addEventListener('scroll', this.resetAtcSuccessHandler, {
      once: true,
      passive: true
    })
  }
}
customElements.define('product-form', ProductForm)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    this.mainElem = this.querySelector('.swiper-main')
    this.mainWrapper = this.mainElem?.querySelector('.swiper-wrapper')
    this.thumbsElem = this.querySelector('.swiper-thumbs')
    this.thumbsWrapper = this.thumbsElem?.querySelector('.swiper-wrapper')

    this.allMainSlides = this.mainWrapper
      ? [...this.mainWrapper.children].map(slide => slide.cloneNode(true))
      : []
    this.allThumbSlides = this.thumbsWrapper
      ? [...this.thumbsWrapper.children].map(slide => slide.cloneNode(true))
      : []
    this.variantFeaturedMediaIds = this.dataset.variantFeaturedMediaIds
      ? this.dataset.variantFeaturedMediaIds
        .split(',')
        .map(id => id.trim())
        .filter(Boolean)
      : []
    this.productFeaturedMediaId = this.dataset.productFeaturedMediaId || ''
    this.productFeaturedMediaSignature = this.dataset.productFeaturedMediaSignature || ''
    this.fullScreenEnabled = this.dataset.fullScreenEnabled === 'true'
    this.fullScreenEffect = this.dataset.fullScreenEffect || 'slide'
    this.variantFeaturedSignatures = [...new Set(
      this.allMainSlides
        .filter(slide => this.variantFeaturedMediaIds.includes(String(slide.dataset.mediaId)))
        .map(slide => slide.dataset.mediaSignature)
        .filter(Boolean)
    )]
    this.fullscreenOverlay = null
    this.fullscreenSwiper = null
    this.fullscreenTapTimeout = null
    this.lastFullscreenTapAt = 0
    this.lastFullscreenGestureAt = 0

    this.setStickyPosition()
    this.listenVariantChange()
    this.rebuildGallery(
      this.dataset.selectedMediaId,
      Number(this.dataset.initialSlide) || 0
    )
    this.initFullscreen()
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView),
      spaceBetween: 10
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: 'bullets',
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      }
    }

    if (this.thumbs) {
      options.thumbs = {
        swiper: this.thumbs,
        autoScrollOffset: 1
      }
    }

    return options
  }

  destroySwipers () {
    this.main?.destroy(true, true)
    this.thumbs?.destroy(true, true)
    this.main = null
    this.thumbs = null
  }

  getDisplaySlides (slides, selectedMediaId) {
    if (!selectedMediaId) return [...slides]

    const selectedSlide = slides.find(slide => slide.dataset.mediaId === String(selectedMediaId))

    if (!selectedSlide) return [...slides]

    const selectedSignature = selectedSlide.dataset.mediaSignature
    const seenSignatures = new Set(selectedSignature ? [selectedSignature] : [])
    const excludedVariantMediaIds = new Set(
      this.variantFeaturedMediaIds.filter(id => id !== String(selectedMediaId))
    )
    const excludedVariantSignatures = new Set(
      this.variantFeaturedSignatures.filter(signature => signature && signature !== selectedSignature)
    )
    const shouldExcludeProductFeaturedMedia =
      this.productFeaturedMediaId &&
      this.productFeaturedMediaId !== String(selectedMediaId)
    const shouldExcludeProductFeaturedSignature =
      this.productFeaturedMediaSignature &&
      this.productFeaturedMediaSignature !== selectedSignature

    const sharedSlides = slides.filter(slide => {
      const mediaId = String(slide.dataset.mediaId)
      const signature = slide.dataset.mediaSignature

      if (mediaId === String(selectedMediaId)) return false
      if (excludedVariantMediaIds.has(mediaId)) return false
      if (shouldExcludeProductFeaturedMedia && mediaId === this.productFeaturedMediaId) return false
      if (signature && excludedVariantSignatures.has(signature)) return false
      if (signature && shouldExcludeProductFeaturedSignature && signature === this.productFeaturedMediaSignature) return false
      if (signature && seenSignatures.has(signature)) return false

      if (signature) seenSignatures.add(signature)
      return true
    })

    return [selectedSlide, ...sharedSlides]
  }

  replaceSlides (wrapper, slides) {
    wrapper.replaceChildren(...slides.map(slide => slide.cloneNode(true)))
  }

  toggleThumbs (slidesCount) {
    if (!this.thumbsElem) return

    if (this.thumbsElem.dataset.showThumbs === 'hide') {
      this.thumbsElem.hidden = true
      return
    }

    this.thumbsElem.hidden = slidesCount < 2
  }

  initSwipers (initialSlide = 0) {
    if (!this.mainElem || !this.mainWrapper) return

    const canShowThumbs = this.thumbsElem && this.thumbsWrapper && !this.thumbsElem.hidden

    if (canShowThumbs) {
      this.thumbs = new window.Swiper(this.thumbsElem, this.thumbsOptions())
    }

    this.main = new window.Swiper(this.mainElem, this.mainOptions())
    this.main.slideTo(initialSlide, 0)
  }

  rebuildGallery (selectedMediaId = '', fallbackInitialSlide = 0) {
    if (!this.mainWrapper || !this.thumbsWrapper) return

    const orderedMainSlides = this.getDisplaySlides(this.allMainSlides, selectedMediaId)
    const orderedThumbSlides = this.getDisplaySlides(this.allThumbSlides, selectedMediaId)
    const initialSlideByMediaId = selectedMediaId
      ? orderedMainSlides.findIndex(slide => slide.dataset.mediaId === String(selectedMediaId))
      : -1
    const initialSlide = initialSlideByMediaId >= 0 ? initialSlideByMediaId : fallbackInitialSlide

    this.destroySwipers()
    this.replaceSlides(this.mainWrapper, orderedMainSlides)
    this.replaceSlides(this.thumbsWrapper, orderedThumbSlides)
    this.toggleThumbs(orderedThumbSlides.length)
    this.dataset.selectedMediaId = selectedMediaId ? String(selectedMediaId) : ''
    this.initSwipers(initialSlide)

    if (this.fullscreenOverlay?.classList.contains('is-open')) {
      this.closeFullscreen()
    }
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        const newScroll = window.scrollY
        if (newScroll > oldScroll) {
          if (newScroll > headerGroup.clientHeight) {
            this.style.top = '20px'
          }
        } else if (newScroll < oldScroll) {
          this.style.top = `${headerGroup.clientHeight + 20}px`
        }

        oldScroll = Math.max(window.scrollY, 0)
      })
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail
      const fallbackInitialSlide = selectedVariant.featured_media
        ? selectedVariant.featured_media.position - 1
        : 0

      this.rebuildGallery(
        selectedVariant.featured_media?.id || '',
        fallbackInitialSlide
      )
    }, false)
  }

  initFullscreen () {
    if (!this.fullScreenEnabled || !this.mainElem) return

    this.mainElem.addEventListener('click', (event) => {
      if (!window.matchMedia('(max-width: 767px)').matches) return
      if (event.target.closest('.swiper-button-prev, .swiper-button-next, .swiper-pagination, .swiper-scrollbar')) return

      const slide = event.target.closest('.swiper-slide')
      if (!slide) return

      const currentSlides = [...this.mainWrapper.children]
      const slideIndex = currentSlides.findIndex(item => item === slide)

      if (slideIndex < 0) return

      event.preventDefault()
      this.openFullscreen(slideIndex)
    })
  }

  buildFullscreenSlidesMarkup () {
    return [...this.mainWrapper.children].map(slide => {
      const image = slide.querySelector('img')
      const video = slide.querySelector('video')

      if (image) {
        return `
          <div class="swiper-slide">
            <div class="swiper-zoom-container">
              <img src="${image.currentSrc || image.src}" alt="${image.alt || ''}" class="product-gallery-lightbox__media" />
            </div>
          </div>
        `
      }

      if (video) {
        return `
          <div class="swiper-slide">
            <div class="product-gallery-lightbox__video">
              ${video.outerHTML}
            </div>
          </div>
        `
      }

      return ''
    }).join('')
  }

  ensureFullscreenOverlay () {
    if (this.fullscreenOverlay) return

    const overlay = document.createElement('div')
    overlay.className = 'product-gallery-lightbox'
    overlay.innerHTML = `
      <button type="button" class="product-gallery-lightbox__close" aria-label="Close">
        ✕
      </button>
      <div class="product-gallery-lightbox__inner">
        <div class="product-gallery-lightbox__swiper swiper">
          <div class="swiper-wrapper"></div>
          <div class="swiper-pagination"></div>
          <div class="swiper-button-prev"></div>
          <div class="swiper-button-next"></div>
        </div>
      </div>
    `

    overlay.querySelector('.product-gallery-lightbox__close')?.addEventListener('click', () => this.closeFullscreen())
    overlay.addEventListener('click', (event) => this.handleFullscreenTap(event))

    document.body.appendChild(overlay)
    this.fullscreenOverlay = overlay
  }

  openFullscreen (initialSlide = 0) {
    this.ensureFullscreenOverlay()

    const swiperWrapper = this.fullscreenOverlay.querySelector('.swiper-wrapper')
    swiperWrapper.innerHTML = this.buildFullscreenSlidesMarkup()

    this.fullscreenSwiper?.destroy(true, true)
    this.fullscreenSwiper = new window.Swiper(this.fullscreenOverlay.querySelector('.product-gallery-lightbox__swiper'), {
      initialSlide,
      speed: Number(this.dataset.speed) || 300,
      effect: this.fullScreenEffect === 'fade' ? 'fade' : 'slide',
      zoom: {
        maxRatio: 3,
        minRatio: 1,
        toggle: false
      },
      pagination: {
        el: this.fullscreenOverlay.querySelector('.swiper-pagination'),
        clickable: true
      },
      navigation: {
        nextEl: this.fullscreenOverlay.querySelector('.swiper-button-next'),
        prevEl: this.fullscreenOverlay.querySelector('.swiper-button-prev')
      },
      on: {
        zoomChange: (swiper, scale) => {
          this.lastFullscreenGestureAt = Date.now()
          swiper.allowTouchMove = scale <= 1.01
        },
        slideChangeTransitionStart: (swiper) => {
          swiper.allowTouchMove = true
          this.lastFullscreenGestureAt = Date.now()
        }
      }
    })

    this.fullscreenOverlay.classList.add('is-open')
    document.body.classList.add('product-gallery-lightbox-open')
  }

  handleFullscreenTap (event) {
    if (!this.fullscreenOverlay?.classList.contains('is-open')) return
    if (event.target.closest('.product-gallery-lightbox__close, .swiper-button-prev, .swiper-button-next, .swiper-pagination')) return

    const slide = event.target.closest('.swiper-slide')
    const now = Date.now()
    const currentScale = this.fullscreenSwiper?.zoom?.scale || 1

    if (now - this.lastFullscreenTapAt < 260) {
      window.clearTimeout(this.fullscreenTapTimeout)
      this.lastFullscreenTapAt = 0
      this.toggleFullscreenZoom(event)
      return
    }

    if (currentScale > 1.01) {
      this.lastFullscreenTapAt = now
      window.clearTimeout(this.fullscreenTapTimeout)
      this.fullscreenTapTimeout = window.setTimeout(() => {
        this.lastFullscreenTapAt = 0
      }, 280)
      return
    }
    if (now - this.lastFullscreenGestureAt < 320) return

    if (!slide) {
      this.closeFullscreen()
      return
    }

    this.lastFullscreenTapAt = now
    this.fullscreenTapTimeout = window.setTimeout(() => {
      this.closeFullscreen()
    }, 220)
  }

  toggleFullscreenZoom (event) {
    if (!this.fullscreenSwiper?.zoom) return

    const zoom = this.fullscreenSwiper.zoom
    const currentScale = zoom.scale || 1
    const activeSlide = this.fullscreenSwiper.slides?.[this.fullscreenSwiper.activeIndex]
    const zoomContainer = activeSlide?.querySelector('.swiper-zoom-container')
    const zoomMedia = activeSlide?.querySelector('img, video')

    if (currentScale > 1) {
      zoom.out()
      if (zoomContainer) zoomContainer.style.transformOrigin = ''
      if (zoomMedia) zoomMedia.style.transformOrigin = ''
      this.lastFullscreenGestureAt = Date.now()
    } else {
      if (event && zoomContainer) {
        const rect = zoomContainer.getBoundingClientRect()
        const x = Math.min(Math.max(event.clientX - rect.left, 0), rect.width)
        const y = Math.min(Math.max(event.clientY - rect.top, 0), rect.height)
        const originX = rect.width ? (x / rect.width) * 100 : 50
        const originY = rect.height ? (y / rect.height) * 100 : 50
        const transformOrigin = `${originX}% ${originY}%`

        zoomContainer.style.transformOrigin = transformOrigin
        if (zoomMedia) zoomMedia.style.transformOrigin = transformOrigin
      }
      zoom.in(2.4)
      this.lastFullscreenGestureAt = Date.now()
    }
  }

  closeFullscreen () {
    window.clearTimeout(this.fullscreenTapTimeout)
    this.lastFullscreenTapAt = 0
    const activeSlide = this.fullscreenSwiper?.slides?.[this.fullscreenSwiper.activeIndex]
    const zoomContainer = activeSlide?.querySelector('.swiper-zoom-container')
    const zoomMedia = activeSlide?.querySelector('img, video')
    if (zoomContainer) zoomContainer.style.transformOrigin = ''
    if (zoomMedia) zoomMedia.style.transformOrigin = ''
    this.fullscreenSwiper?.zoom?.out?.()
    this.fullscreenSwiper?.destroy(true, true)
    this.fullscreenSwiper = null
    this.fullscreenOverlay?.classList.remove('is-open')
    document.body.classList.remove('product-gallery-lightbox-open')
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}

const initProductTitleToggle = (root = document) => {
  root.querySelectorAll('[data-product-title-block]').forEach(block => {
    if (block.dataset.titleToggleReady === 'true') return

    const title = block.querySelector('[data-product-title]')
    const toggle = block.querySelector('[data-product-title-toggle]')

    if (!title || !toggle) return

    let isExpanded = false

    const sync = () => {
      const isMobile = window.matchMedia('(max-width: 767px)').matches

      if (!isMobile) {
        title.classList.remove('is-collapsed', 'is-expanded')
        toggle.hidden = true
        toggle.setAttribute('aria-expanded', 'false')
        return
      }

      title.classList.toggle('is-collapsed', !isExpanded)
      title.classList.toggle('is-expanded', isExpanded)

      window.requestAnimationFrame(() => {
        const hasOverflow = title.scrollHeight > title.clientHeight + 2 || isExpanded
        toggle.hidden = !hasOverflow
        toggle.textContent = isExpanded ? '閉じる' : 'もっと見る'
        toggle.setAttribute('aria-expanded', String(isExpanded))
      })
    }

    toggle.addEventListener('click', () => {
      isExpanded = !isExpanded
      sync()
    })

    block.dataset.titleToggleReady = 'true'
    sync()
    window.addEventListener('resize', sync)
  })
}

const initProductChatLauncher = () => {
  if (document.body.dataset.productChatLauncherReady === 'true') return

  const collapseLauncher = () => {
    document.body.classList.remove('product-chat-launcher-expanded')
  }

  document.addEventListener('click', (event) => {
    const launcher = event.target.closest('inbox-online-store-chat')
    if (!launcher || launcher.getAttribute('is-open') === 'true') return

    if (!document.body.classList.contains('product-chat-launcher-expanded')) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation?.()
      document.body.classList.add('product-chat-launcher-expanded')
    }
  }, true)

  window.addEventListener('scroll', collapseLauncher, { passive: true })
  document.body.dataset.productChatLauncherReady = 'true'
}

adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)
initProductTitleToggle()
initProductChatLauncher()
window.addEventListener('kt.product.quick_view.modal_shown', () => initProductTitleToggle(document))
